import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import Home from './pages/Home'
import PoolPage from './pages/PoolPage'
import CreatePool from './pages/CreatePool'
import Pools from './pages/Pools'
import Roadmap from './pages/Roadmap'
import AboutUs from './pages/AboutUs'
import TermsOfService from './pages/TermsOfService'
import DebugPanel from './components/DebugPanel'
import useDebug from './hooks/useDebug'

/**
 * Main App Component with enhanced routing and layout integration
 * This is the root component that handles navigation between different pages
 * and integrates our premium Layout wrapper with BackgroundEffects
 */

/**
 * Main App component with enhanced routing configuration
 * Integrates the premium Layout component and enhanced pages
 */
function App(): JSX.Element {
  const debug = useDebug('App', {
    enabled: true,
    logMounts: true,
    logRenders: true,
    trackPerformance: true
  })

  debug.timeStart('render')
  debug.info('🚀 Component rendering', {
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search
  })
  
  try {
    debug.info('✅ Rendering Layout and Routes')
    debug.timeEnd('render')
    
    return (
      <>
        <Layout>
          <Routes>
            {/* Home/Landing page with enhanced UI */}
            <Route path="/" element={<Home />} />
            
            {/* Individual pool page */}
            <Route path="/pool/:tokenMint" element={<PoolPage />} />
            
            {/* Create new pool page */}
            <Route path="/create-pool" element={<CreatePool />} />
            
            {/* Search and browse pools page */}
            <Route path="/pools" element={<Pools />} />
            
            {/* WhitePaper & Roadmap page */}
            <Route path="/roadmap" element={<Roadmap />} />
            
            {/* About us page */}
            <Route path="/about" element={<AboutUs />} />

            {/* Terms of Service page */}
            <Route path="/terms" element={<TermsOfService />} />
            
            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
        
        {/* Debug Panel - Toggle with bug icon */}
        <DebugPanel defaultOpen={false} position="bottom-right" />
      </>
    )
  } catch (error) {
    debug.error('❌ Component error', error)
    
    // Fallback rendering
    return (
      <div className="min-h-screen bg-dark-bg text-text-primary p-8">
        <div className="text-center">
          <h1 className="text-2xl text-cyber-cyan mb-4">App Error</h1>
          <p className="text-text-secondary">Something went wrong loading the app.</p>
          <pre className="text-xs text-status-error mt-4 bg-dark-card p-4 rounded">
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </div>
      </div>
    )
  }
}

export default App